import React from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Spinner from 'react-bootstrap/Spinner';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import SettingsIcon from '@mui/icons-material/Settings';
import SelectInput from '../inputs/select';
import CustomInput from '../inputs/new-input';

/**
 * Accordion panel component allowing users to override default accounts, tax rates,
 * line squashing, and exchange rate parameters prior to initiating an invoice/bill push sync.
 *
 * @param {Object} props
 * @param {Object} props.selectedConnection - Currently selected platform connection
 * @param {'invoice'|'bill'} props.entityType - Entity type being pushed
 * @param {string} [props.billTab] - Current bill subtab ('supplier'|'contractor')
 * @param {string} props.invoiceStatus - Selected default invoice status
 * @param {Function} props.setInvoiceStatus - Invoice status setter
 * @param {string} props.purchaseOrderStatus - Selected default PO/bill status
 * @param {Function} props.setPurchaseOrderStatus - PO status setter
 * @param {boolean} props.squashLineItems - Line squashing toggle state
 * @param {Function} props.setSquashLineItems - Line squashing setter
 * @param {string} props.lineAmountTypes - Tax mode ('Exclusive'|'Inclusive')
 * @param {Function} props.setLineAmountTypes - Tax mode setter
 * @param {string} props.accountCode - Selected revenue account code
 * @param {Function} props.setAccountCode - Revenue account setter
 * @param {string} props.defaultItemId - Selected QBO default item ID
 * @param {Function} props.setDefaultItemId - QBO item setter
 * @param {string} props.cogsAccountCode - Selected supplier COGS account code
 * @param {Function} props.setCogsAccountCode - COGS account setter
 * @param {string} props.contractorAccountCode - Selected subcontractor account code
 * @param {Function} props.setContractorAccountCode - Subcontractor account setter
 * @param {string} props.taxType - Selected sales tax code
 * @param {Function} props.setTaxType - Sales tax setter
 * @param {string} props.expenseTaxType - Selected expense tax code
 * @param {Function} props.setExpenseTaxType - Expense tax setter
 * @param {string|number} props.exchangeRate - Exchange rate multiplier string
 * @param {Function} props.setExchangeRate - Exchange rate setter
 * @param {Array<Object>} props.accountsList - Platform chart of accounts metadata
 * @param {Array<Object>} props.taxRatesList - Platform tax rates metadata
 * @param {Array<Object>} [props.itemsList] - Platform product/service items metadata
 * @param {Array<Object>} props.invoiceStatusesList - Supported invoice status options
 * @param {Array<Object>} props.poStatusesList - Supported PO status options
 * @param {Array<Object>} props.invoiceStatusItems - Fallback invoice status options
 * @param {boolean} props.loadingMetadata - Whether platform metadata is fetching
 * @param {boolean} props.syncing - Whether a sync operation is currently active
 * @returns {React.ReactElement|null}
 */
export default function SyncInvoicesOverrides({
    selectedConnection,
    entityType,
    billTab,
    invoiceStatus,
    setInvoiceStatus,
    purchaseOrderStatus,
    setPurchaseOrderStatus,
    squashLineItems,
    setSquashLineItems,
    lineAmountTypes,
    setLineAmountTypes,
    accountCode,
    setAccountCode,
    defaultItemId,
    setDefaultItemId,
    cogsAccountCode,
    setCogsAccountCode,
    contractorAccountCode,
    setContractorAccountCode,
    taxType,
    setTaxType,
    expenseTaxType,
    setExpenseTaxType,
    exchangeRate,
    setExchangeRate,
    accountsList,
    taxRatesList,
    itemsList = [],
    invoiceStatusesList,
    poStatusesList,
    invoiceStatusItems,
    loadingMetadata,
    syncing
}) {
    if (!selectedConnection) return null;

    return (
        <Accordion className="mb-3" data-cy="sync-invoices-override-accordion">
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    <span className="font-weight-bold text-dark small text-uppercase override-accordion-title d-inline-flex align-items-center">
                        <SettingsIcon className="me-1 brand-icon icon-small" /> Override Integration Settings (Optional)
                    </span>
                </Accordion.Header>
                <Accordion.Body className="p-3 bg-light">
                    {loadingMetadata ? (
                        <div className="py-3 text-center text-muted small">
                            <Spinner size="sm" animation="border" className="me-2" />
                            Loading connection accounts & tax rates...
                        </div>
                    ) : (
                        <>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <SelectInput
                                        label={entityType === 'invoice' ? "Default Invoice(Progress Claim) Status" : "Default Bill(Vendor Invoices) Status"}
                                        value={entityType === 'invoice' ? invoiceStatus : purchaseOrderStatus}
                                        onChange={(e) => entityType === 'invoice' ? setInvoiceStatus(e.target.value) : setPurchaseOrderStatus(e.target.value)}
                                        disabled={syncing}
                                        dataCy="sync-invoices-override-status"
                                        items={((entityType === 'invoice' ? invoiceStatusesList : poStatusesList).length > 0
                                            ? (entityType === 'invoice' ? invoiceStatusesList : poStatusesList)
                                            : invoiceStatusItems
                                        ).map(opt => ({ value: opt.value, key: opt.label || opt.key || opt.value }))}
                                    />
                                    {(entityType === 'invoice' ? invoiceStatus : purchaseOrderStatus) === 'AUTHORISED' && (
                                        <div className="mt-1 small brand-color-5 font-weight-500">
                                            Note: Once pushed as <strong>Awaiting Payment</strong>, this transaction cannot be reverted to Draft or Awaiting Approval.
                                        </div>
                                    )}
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="align-with-inputs">
                                        <Form.Check
                                            type="checkbox"
                                            label={entityType === 'invoice' ? "Squash line items for Invoices (AR)" : "Squash line items for Bills (AP)"}
                                            checked={squashLineItems}
                                            onChange={(e) => setSquashLineItems(e.target.checked)}
                                            disabled={syncing}
                                            data-cy="sync-invoices-override-squash"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    {entityType === 'invoice' ? (
                                        selectedConnection?.platform === 'quickbooks' ? (
                                            <SelectInput
                                                label="Product / Service"
                                                value={defaultItemId}
                                                onChange={(e) => setDefaultItemId(e.target.value)}
                                                disabled={syncing}
                                                dataCy="sync-invoices-override-item"
                                                items={(itemsList || []).map(item => ({
                                                    value: item.code,
                                                    key: `${item.name}${item.taxable ? ' (Taxable)' : ' (Non-Taxable)'}`,
                                                    label: `${item.name}${item.taxable ? ' (Taxable)' : ' (Non-Taxable)'}`
                                                }))}
                                            />
                                        ) : (
                                            <SelectInput
                                                label="Revenue Account"
                                                value={accountCode}
                                                onChange={(e) => setAccountCode(e.target.value)}
                                                disabled={syncing}
                                                dataCy="sync-invoices-override-account"
                                                items={accountsList.map(a => ({ value: a.code, key: `${a.code} - ${a.name}` }))}
                                            />
                                        )
                                    ) : billTab === 'contractor' ? (
                                        <>
                                            <SelectInput
                                                label="Contractor Account (Subcontractors)"
                                                value={contractorAccountCode}
                                                onChange={(e) => setContractorAccountCode(e.target.value)}
                                                disabled={syncing}
                                                dataCy="sync-invoices-override-contractor-account"
                                                items={accountsList.map(a => ({ value: a.code, key: `${a.code} - ${a.name}` }))}
                                            />
                                            <span className="d-block mt-1 small italic-subtext">
                                                Used for Subcontractor bills.
                                            </span>
                                        </>
                                    ) : (
                                        <>
                                            <SelectInput
                                                label="Supplier Account (COGS)"
                                                value={cogsAccountCode}
                                                onChange={(e) => setCogsAccountCode(e.target.value)}
                                                disabled={syncing}
                                                dataCy="sync-invoices-override-account"
                                                items={accountsList.map(a => ({ value: a.code, key: `${a.code} - ${a.name}` }))}
                                            />
                                            <span className="d-block mt-1 small italic-subtext">
                                                Used for Supplier invoices (COGS/materials).
                                            </span>
                                        </>
                                    )}
                                </Col>
                                <Col md={6}>
                                    {entityType === 'bill' && selectedConnection?.platform === 'quickbooks' ? (
                                        <SelectInput
                                            label="Tax Calculation Mode"
                                            value={lineAmountTypes}
                                            onChange={(e) => setLineAmountTypes(e.target.value)}
                                            disabled={syncing}
                                            dataCy="sync-invoices-override-tax-calc-mode"
                                            items={[
                                                { value: "Exclusive", key: "Tax Exclusive", label: "Tax Exclusive" },
                                                { value: "Inclusive", key: "Tax Inclusive", label: "Tax Inclusive" }
                                            ]}
                                        />
                                    ) : entityType === 'bill' ? (
                                        <SelectInput
                                            label="Standard Expense Tax Type"
                                            value={expenseTaxType}
                                            onChange={(e) => setExpenseTaxType(e.target.value)}
                                            disabled={syncing}
                                            dataCy="sync-invoices-override-tax"
                                            items={taxRatesList.map(t => ({
                                                value: t.code || t.id || t.name,
                                                key: `${t.name} (${(t.rate !== undefined ? t.rate : t.effectiveRate) !== undefined ? `${t.rate !== undefined ? t.rate : t.effectiveRate}%` : 'Tax Rate'})`
                                            }))}
                                        />
                                    ) : (
                                        <SelectInput
                                            label="Standard Sales Tax Type"
                                            value={taxType}
                                            onChange={(e) => setTaxType(e.target.value)}
                                            disabled={syncing}
                                            dataCy="sync-invoices-override-tax"
                                            items={taxRatesList.map(t => ({
                                                value: t.code || t.id || t.name,
                                                key: `${t.name} (${(t.rate !== undefined ? t.rate : t.effectiveRate) !== undefined ? `${t.rate !== undefined ? t.rate : t.effectiveRate}%` : 'Tax Rate'})`
                                            }))}
                                        />
                                    )}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                {!(entityType === 'bill' && selectedConnection?.platform === 'quickbooks') && (
                                    <Col md={6}>
                                        <SelectInput
                                            label="Tax Calculation Mode"
                                            value={lineAmountTypes}
                                            onChange={(e) => setLineAmountTypes(e.target.value)}
                                            disabled={syncing}
                                            dataCy="sync-invoices-override-tax-calc-mode"
                                            items={[
                                                { value: "Exclusive", key: "Tax Exclusive", label: "Tax Exclusive" },
                                                { value: "Inclusive", key: "Tax Inclusive", label: "Tax Inclusive" }
                                            ]}
                                        />
                                    </Col>
                                )}
                                <Col md={6}>
                                    <CustomInput
                                        label="Exchange Rate"
                                        placeholder="Exchange Rate"
                                        type="number"
                                        value={exchangeRate}
                                        onChange={(e) => setExchangeRate(e.target.value)}
                                        disabled={syncing}
                                        dataCy="sync-invoices-override-exchange-rate"
                                    />
                                    <span className="d-block mt-1 small italic-subtext">
                                        Used for multi-currency transactions. Defaults to 1.0.
                                    </span>
                                </Col>
                            </Row>
                        </>
                    )}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}
